

import nacl from 'tweetnacl';
import bs58 from 'bs58';

// Generate proper encryption keys using NaCl
export const generateEncryptionKeys = () => {
  const keyPair = nacl.box.keyPair();
  
  return {
    privateKey: bs58.encode(keyPair.secretKey),
    publicKey: bs58.encode(keyPair.publicKey)
  };
};

export interface PhantomConnectParams {
  appUrl: string;
  redirectUrl: string;
  publicKey: string;
  cluster?: 'mainnet-beta' | 'devnet' | 'testnet';
}

export const createPhantomConnectUrl = (params: PhantomConnectParams): string => {
  const { appUrl, redirectUrl, publicKey, cluster = 'mainnet-beta' } = params;
  
  const connectUrl = new URL('https://phantom.app/ul/v1/connect');
  connectUrl.searchParams.set('app_url', appUrl);
  connectUrl.searchParams.set('redirect_link', redirectUrl);
  connectUrl.searchParams.set('dapp_encryption_public_key', publicKey);
  connectUrl.searchParams.set('cluster', cluster);
  
  console.log('Created Phantom connect URL:', connectUrl.toString());
  return connectUrl.toString();
};

export const decryptPhantomResponse = (encryptedData: string, privateKey: string): { publicKey: string } | null => {
  try {
    console.log('Attempting to decrypt Phantom response...');
    
    // Decode the encrypted data and private key from base58
    const encryptedBytes = bs58.decode(encryptedData);
    const privateKeyBytes = bs58.decode(privateKey);
    
    // Extract nonce (first 24 bytes) and encrypted message (rest)
    const nonce = encryptedBytes.slice(0, 24);
    const encryptedMessage = encryptedBytes.slice(24);
    
    // Get the public key that was used for encryption
    const storedPublicKey = localStorage.getItem('phantom_public_key');
    if (!storedPublicKey) {
      throw new Error('Public key not found');
    }
    
    const publicKeyBytes = bs58.decode(storedPublicKey);
    
    // Decrypt the message
    const decryptedBytes = nacl.box.open(encryptedMessage, nonce, publicKeyBytes, privateKeyBytes);
    
    if (!decryptedBytes) {
      throw new Error('Failed to decrypt message');
    }
    
    // Parse the decrypted JSON
    const decryptedData = JSON.parse(new TextDecoder().decode(decryptedBytes));
    
    console.log('Successfully decrypted wallet data:', decryptedData);
    
    return {
      publicKey: decryptedData.public_key || decryptedData.publicKey
    };
  } catch (error) {
    console.error('Failed to decrypt Phantom response:', error);
    
    // Fallback: try to parse as unencrypted data (for testing)
    try {
      const fallbackData = JSON.parse(encryptedData);
      if (fallbackData.public_key || fallbackData.publicKey) {
        console.log('Using fallback unencrypted data');
        return {
          publicKey: fallbackData.public_key || fallbackData.publicKey
        };
      }
    } catch (fallbackError) {
      console.error('Fallback parsing also failed:', fallbackError);
    }
    
    return null;
  }
};

export const openPhantomInTelegram = (connectUrl: string): void => {
  console.log('Opening Phantom connect URL in Telegram:', connectUrl);
  
  // Use Telegram WebApp API if available
  if (window.Telegram?.WebApp?.openLink) {
    console.log('Using Telegram WebApp openLink');
    window.Telegram.WebApp.openLink(connectUrl);
  } else if (window.Telegram?.WebApp) {
    console.log('Using Telegram WebApp with fallback');
    // Alternative Telegram method
    window.open(connectUrl, '_blank');
  } else {
    console.log('Fallback for non-Telegram environments');
    window.open(connectUrl, '_blank');
  }
};

// Configuration for bot username - make it configurable
const DEFAULT_BOT_USERNAME = 'BroRankBot'; // Replace with your actual bot username

export const createTelegramReturnUrl = (botUsername: string = DEFAULT_BOT_USERNAME, walletAddress: string): string => {
  const encodedWallet = btoa(walletAddress);
  return `https://t.me/${botUsername}?startapp=wallet_connected_${encodedWallet}`;
};

export const parseWalletFromStartParam = (startParam: string): string | null => {
  if (!startParam || !startParam.startsWith('wallet_connected_')) {
    return null;
  }
  
  try {
    const encodedWallet = startParam.replace('wallet_connected_', '');
    return atob(encodedWallet);
  } catch (error) {
    console.error('Failed to parse wallet from start param:', error);
    return null;
  }
};
